import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { 
  DollarSign, Users, Shield, FileText, LogOut, CheckCircle, XCircle, Clock, Eye, 
  TrendingUp, Package, Activity, UserCheck, Building2, Camera, Plus, Trash2, 
  Printer, Download, Upload, UserPlus, Edit, Search, Filter, X, Image, Gift, Crown, Star, Award, Hash, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { adminAPI } from '@/lib/api';

const COLORS = ['#0f172a', '#ea580c', '#14b8a6', '#8b5cf6', '#f43f5e', '#06b6d4', '#84cc16', '#f59e0b'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [analytics, setAnalytics] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState({ face_verifications: [], business_verifications: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const printRef = useRef();
  
  // Modal states
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [verificationDecision, setVerificationDecision] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [decidingLoading, setDecidingLoading] = useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null);
  
  // Add User Form
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'buyer',
    business_type: 'individual',
    verified_email: true,
    verified_phone: false
  });

  const [referralStats, setReferralStats] = useState(null);
  const [hsCodeAnalytics, setHsCodeAnalytics] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, pendingRes, usersRes] = await Promise.all([
        adminAPI.getAnalytics(),
        adminAPI.getPendingVerifications(),
        adminAPI.getUsers()
      ]);
      setAnalytics(analyticsRes.data);
      setPendingVerifications(pendingRes.data);
      setAllUsers(usersRes.data);
      
      // Fetch referral stats
      try {
        const refStatsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/referrals/stats`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (refStatsRes.ok) {
          const refStats = await refStatsRes.json();
          setReferralStats(refStats);
        }
      } catch (refError) {
        console.log('Referral stats not available');
      }
      
      // Fetch HS Code analytics
      try {
        const hsAnalyticsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/hs-codes/analytics`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (hsAnalyticsRes.ok) {
          const hsAnalytics = await hsAnalyticsRes.json();
          setHsCodeAnalytics(hsAnalytics);
        }
      } catch (hsError) {
        console.log('HS Code analytics not available');
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const openDecisionModal = (verification, type, decision) => {
    setSelectedVerification({ ...verification, type });
    setVerificationDecision(decision);
    setDeclineReason('');
    setShowDecisionModal(true);
  };

  const openDocumentPreview = (verification, type) => {
    setSelectedVerification({ ...verification, type });
    setShowDocumentPreview(true);
  };

  const handleDecision = async () => {
    if (!selectedVerification) return;
    
    setDecidingLoading(true);
    try {
      const apiCall = selectedVerification.type === 'face' 
        ? adminAPI.decideFaceVerification 
        : adminAPI.decideBusinessVerification;
      
      await apiCall(
        selectedVerification.id, 
        verificationDecision, 
        verificationDecision === 'decline' ? declineReason : null
      );
      
      toast.success(`Verification ${verificationDecision === 'approve' ? 'approved' : 'declined'}`);
      setShowDecisionModal(false);
      setShowDocumentPreview(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process decision');
    } finally {
      setDecidingLoading(false);
    }
  };

  const handleAddUser = async () => {
    try {
      await adminAPI.createUser(newUserForm);
      toast.success('User created successfully');
      setShowAddUserModal(false);
      setNewUserForm({
        email: '',
        password: '',
        full_name: '',
        role: 'buyer',
        business_type: 'individual',
        verified_email: true,
        verified_phone: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUserToDelete) return;
    try {
      await adminAPI.deleteUser(selectedUserToDelete.id);
      toast.success('User deleted successfully');
      setShowDeleteUserModal(false);
      setSelectedUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('print-section');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>ServPac - Admin Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { color: #0f172a; }
            .metric { display: inline-block; margin: 20px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; }
            .metric-value { font-size: 32px; font-weight: bold; color: #0f172a; }
            .metric-label { color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background: #f5f5f5; }
            .header { border-bottom: 2px solid #0f172a; padding-bottom: 20px; margin-bottom: 30px; }
            .section { margin-top: 30px; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ServPac - Executive Report</h1>
            <p>Generated on: ${new Date().toLocaleString()}</p>
          </div>
          
          <div class="section">
            <h2>Financial Summary</h2>
            <div class="metric">
              <div class="metric-value">$${analytics?.revenue?.total?.toFixed(2) || '0.00'}</div>
              <div class="metric-label">Total Revenue</div>
            </div>
            <div class="metric">
              <div class="metric-value">${analytics?.revenue?.transactions || 0}</div>
              <div class="metric-label">Transactions</div>
            </div>
            <div class="metric">
              <div class="metric-value">$${((analytics?.api_costs?.sms || 0) + (analytics?.api_costs?.face_verification || 0)).toFixed(2)}</div>
              <div class="metric-label">Operating Costs</div>
            </div>
            <div class="metric">
              <div class="metric-value">$${((analytics?.revenue?.total || 0) - (analytics?.api_costs?.sms || 0) - (analytics?.api_costs?.face_verification || 0)).toFixed(2)}</div>
              <div class="metric-label">Net Profit</div>
            </div>
          </div>
          
          <div class="section">
            <h2>User Statistics</h2>
            <table>
              <tr><th>Metric</th><th>Value</th></tr>
              <tr><td>Total Users</td><td>${analytics?.users?.total || 0}</td></tr>
              <tr><td>Buyers</td><td>${analytics?.users?.buyers || 0}</td></tr>
              <tr><td>Sellers</td><td>${analytics?.users?.sellers || 0}</td></tr>
              <tr><td>Verified Users</td><td>${analytics?.users?.verified || 0}</td></tr>
              <tr><td>Active Subscriptions</td><td>${analytics?.subscriptions?.active || 0}</td></tr>
            </table>
          </div>
          
          <div class="section">
            <h2>Platform Activity</h2>
            <table>
              <tr><th>Metric</th><th>Value</th></tr>
              <tr><td>Total Quotes</td><td>${analytics?.quotes?.total || 0}</td></tr>
              <tr><td>Completed Quotes</td><td>${analytics?.quotes?.completed || 0}</td></tr>
              <tr><td>Total Products</td><td>${analytics?.products?.total || 0}</td></tr>
              <tr><td>Face Verifications (Pending/Approved)</td><td>${analytics?.verifications?.face?.pending || 0} / ${analytics?.verifications?.face?.approved || 0}</td></tr>
              <tr><td>Business Verifications (Pending/Approved)</td><td>${analytics?.verifications?.business?.pending || 0} / ${analytics?.verifications?.business?.approved || 0}</td></tr>
            </table>
          </div>
          
          <div class="section" style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd;">
            <p style="color: #666; font-size: 12px;">
              This report is confidential and intended for sponsors and investors only.<br/>
              ServPac © ${new Date().getFullYear()}
            </p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Filter users based on search
  const filteredUsers = allUsers.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  const userDistribution = [
    { name: 'Buyers', value: analytics?.users?.buyers || 0, color: '#0f172a' },
    { name: 'Sellers', value: analytics?.users?.sellers || 0, color: '#ea580c' }
  ];

  const totalPending = (analytics?.verifications?.face?.pending || 0) + (analytics?.verifications?.business?.pending || 0);
  
  // P&L Data
  const revenue = analytics?.revenue?.total || 0;
  const costs = (analytics?.api_costs?.sms || 0) + (analytics?.api_costs?.face_verification || 0);
  const netProfit = revenue - costs;

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/servpac-logo.png" alt="ServPac" className="w-8 h-8 object-contain" />
              <h1 className="text-2xl font-chivo font-bold text-ocean-deep">ServPac Admin</h1>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-report-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print Report
              </Button>
              <span className="text-sm text-slate-600">{user.full_name}</span>
              <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-8" id="print-section">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-chivo font-bold text-slate-900">Admin Dashboard</h2>
            <p className="text-slate-600">Monitor performance, manage users, and view analytics</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-3xl grid-cols-6">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <Activity className="w-4 h-4 mr-1" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="verifications" data-testid="tab-verifications" className="relative">
              <Shield className="w-4 h-4 mr-1" />
              Verify
              {totalPending > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {totalPending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals">
              <Gift className="w-4 h-4 mr-1" />
              Referrals
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="w-4 h-4 mr-1" />
              Users
            </TabsTrigger>
            <TabsTrigger value="pnl" data-testid="tab-pnl">
              <DollarSign className="w-4 h-4 mr-1" />
              P&L
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <TrendingUp className="w-4 h-4 mr-1" />
              Charts
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card data-testid="total-revenue-card" className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <DollarSign className="w-8 h-8 text-green-600 mb-2" />
                <h3 className="text-sm font-medium text-green-800 mb-1">Total Revenue</h3>
                <p className="text-3xl font-chivo font-black text-green-900">
                  ${revenue.toFixed(2)}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {analytics?.revenue?.transactions || 0} transactions
                </p>
              </Card>

              <Card data-testid="total-users-card" className="p-6">
                <Users className="w-8 h-8 text-blue-600 mb-2" />
                <h3 className="text-sm font-medium text-slate-600 mb-1">Total Users</h3>
                <p className="text-3xl font-chivo font-black text-slate-900">
                  {analytics?.users?.total || 0}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {analytics?.users?.verified || 0} verified
                </p>
              </Card>

              <Card data-testid="pending-verifications-card" className="p-6">
                <Clock className="w-8 h-8 text-amber-600 mb-2" />
                <h3 className="text-sm font-medium text-slate-600 mb-1">Pending Verifications</h3>
                <p className="text-3xl font-chivo font-black text-slate-900">
                  {totalPending}
                </p>
              </Card>

              <Card data-testid="net-profit-card" className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <TrendingUp className="w-8 h-8 text-blue-600 mb-2" />
                <h3 className="text-sm font-medium text-blue-800 mb-1">Net Profit</h3>
                <p className={`text-3xl font-chivo font-black ${netProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  ${netProfit.toFixed(2)}
                </p>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">User Breakdown</h3>
                <div className="space-y-3">
                  <div className="border-b pb-2 mb-2">
                    <p className="text-xs text-slate-500 uppercase font-medium mb-2">Buyers</p>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Individual Buyers</span><strong>{analytics?.users?.breakdown?.individual_buyers || 0}</strong></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Business Buyers</span><strong>{analytics?.users?.breakdown?.business_buyers || 0}</strong></div>
                  </div>
                  <div className="border-b pb-2 mb-2">
                    <p className="text-xs text-slate-500 uppercase font-medium mb-2">Sellers</p>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Individual Sellers</span><strong>{analytics?.users?.breakdown?.individual_sellers || 0}</strong></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Business Sellers</span><strong>{analytics?.users?.breakdown?.business_sellers || 0}</strong></div>
                  </div>
                  <div className="flex justify-between pt-1"><span className="font-medium">Total Verified</span><strong className="text-green-600">{analytics?.users?.verified || 0}</strong></div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Verifications</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><span>Face Approved</span><strong className="text-green-600">{analytics?.verifications?.face?.approved || 0}</strong></div>
                  <div className="flex justify-between"><span>Face Pending</span><strong className="text-amber-600">{analytics?.verifications?.face?.pending || 0}</strong></div>
                  <div className="flex justify-between"><span>Business Approved</span><strong className="text-green-600">{analytics?.verifications?.business?.approved || 0}</strong></div>
                  <div className="flex justify-between"><span>Business Pending</span><strong className="text-amber-600">{analytics?.verifications?.business?.pending || 0}</strong></div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Platform Activity</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><span>Total Quotes</span><strong>{analytics?.quotes?.total || 0}</strong></div>
                  <div className="flex justify-between"><span>Completed</span><strong className="text-green-600">{analytics?.quotes?.completed || 0}</strong></div>
                  <div className="flex justify-between"><span>Products</span><strong>{analytics?.products?.total || 0}</strong></div>
                  <div className="flex justify-between"><span>Subscriptions</span><strong>{analytics?.subscriptions?.active || 0}</strong></div>
                  <div className="flex justify-between"><span>Referral Sign-ups</span><strong className="text-purple-600">{analytics?.referrals?.total || 0}</strong></div>
                </div>
              </Card>
            </div>

            {/* New Row: Product Categories, Pacific Nations, Buyer Regions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Product Categories */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Products by Category</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {analytics?.products?.by_category && Object.keys(analytics.products.by_category).length > 0 ? (
                    Object.entries(analytics.products.by_category).map(([category, products]) => (
                      <div key={category} className="border-b pb-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">{category}</span>
                          <span className="text-xs bg-slate-100 px-2 py-1 rounded">{products.length}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {products.slice(0, 3).join(', ')}{products.length > 3 ? '...' : ''}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No products registered yet</p>
                  )}
                </div>
              </Card>

              {/* Pacific Island Nations */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Users by Pacific Nation</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {analytics?.users_by_pacific_nation && Object.keys(analytics.users_by_pacific_nation).length > 0 ? (
                    Object.entries(analytics.users_by_pacific_nation).map(([nation, counts]) => (
                      <div key={nation} className="flex justify-between items-center text-sm">
                        <span>{nation}</span>
                        <div className="flex gap-2">
                          <span className="text-blue-600" title="Sellers">{counts.sellers}S</span>
                          <span className="text-green-600" title="Buyers">{counts.buyers}B</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No users with country data yet</p>
                  )}
                </div>
              </Card>

              {/* Buyers by World Region */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Buyers by Region</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {analytics?.buyers_by_region && Object.entries(analytics.buyers_by_region)
                    .filter(([_, count]) => count > 0)
                    .map(([region, count]) => (
                      <div key={region} className="flex justify-between items-center text-sm">
                        <span>{region}</span>
                        <strong className="text-slate-700">{count}</strong>
                      </div>
                    ))}
                  {analytics?.buyers_by_region && Object.values(analytics.buyers_by_region).every(v => v === 0) && (
                    <p className="text-sm text-slate-500">No buyer region data yet</p>
                  )}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* VERIFICATIONS TAB */}
          <TabsContent value="verifications" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Face Verifications */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center">
                    <Camera className="w-5 h-5 mr-2 text-blue-600" />
                    Face Verifications
                  </h3>
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                    {pendingVerifications.face_verifications.length} pending
                  </span>
                </div>
                
                {pendingVerifications.face_verifications.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No pending face verifications</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {pendingVerifications.face_verifications.map((v) => (
                      <div key={v.id} className="border rounded-lg p-4 bg-slate-50">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-medium">{v.user?.full_name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{v.user?.email}</p>
                            <p className="text-xs text-slate-400">ID: {v.id_type || 'Not specified'}</p>
                          </div>
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded">{v.status}</span>
                        </div>
                        <p className="text-xs text-slate-500 mb-3">Submitted: {new Date(v.submitted_at).toLocaleString()}</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openDocumentPreview(v, 'face')}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openDecisionModal(v, 'face', 'approve')}>
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => openDecisionModal(v, 'face', 'decline')}>
                            <XCircle className="w-4 h-4 mr-1" /> Decline
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Business Verifications */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-purple-600" />
                    Business Verifications
                  </h3>
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                    {pendingVerifications.business_verifications.length} pending
                  </span>
                </div>
                
                {pendingVerifications.business_verifications.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No pending business verifications</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {pendingVerifications.business_verifications.map((v) => (
                      <div key={v.id} className="border rounded-lg p-4 bg-slate-50">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-medium">{v.business_name || v.user?.full_name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{v.business_email || v.user?.email}</p>
                            {v.country && (
                              <p className="text-xs text-purple-600 mt-1 flex items-center">
                                <Globe className="w-3 h-3 mr-1" />{v.country}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">Doc: {v.document_type?.replace('_', ' ')}</p>
                          </div>
                          <div className="text-right">
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded">{v.status}</span>
                            {v.email_verified && (
                              <p className="text-xs text-green-600 mt-1 flex items-center justify-end">
                                <CheckCircle className="w-3 h-3 mr-1" />Email Verified
                              </p>
                            )}
                          </div>
                        </div>
                        {(v.registration_number || v.tax_number) && (
                          <div className="flex gap-4 text-xs text-slate-600 mb-2 bg-slate-100 p-2 rounded">
                            {v.registration_number && <span>Reg#: {v.registration_number}</span>}
                            {v.tax_number && <span>Tax#: {v.tax_number}</span>}
                          </div>
                        )}
                        {(v.established_date || v.expiry_date) && (
                          <div className="flex gap-4 text-xs text-slate-500 mb-2">
                            {v.established_date && <span>Est: {v.established_date}</span>}
                            {v.expiry_date && <span>Exp: {v.expiry_date}</span>}
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mb-3">Submitted: {new Date(v.submitted_at).toLocaleString()}</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openDocumentPreview(v, 'business')}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openDecisionModal(v, 'business', 'approve')}>
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => openDecisionModal(v, 'business', 'decline')}>
                            <XCircle className="w-4 h-4 mr-1" /> Decline
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* REFERRALS TAB - Founding Growth Partner Program */}
          <TabsContent value="referrals" className="space-y-6">
            {/* Program Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <Gift className="w-6 h-6 text-amber-600 mb-2" />
                <p className="text-sm text-amber-700">Total Referrals</p>
                <p className="text-2xl font-bold text-amber-900">{referralStats?.summary?.total_referrals || 0}</p>
              </Card>
              <Card className="p-4">
                <Clock className="w-6 h-6 text-blue-600 mb-2" />
                <p className="text-sm text-slate-600">Pending</p>
                <p className="text-2xl font-bold">{referralStats?.summary?.pending || 0}</p>
              </Card>
              <Card className="p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mb-2" />
                <p className="text-sm text-slate-600">Qualified</p>
                <p className="text-2xl font-bold text-green-600">{referralStats?.summary?.qualified || 0}</p>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <Crown className="w-6 h-6 text-purple-600 mb-2" />
                <p className="text-sm text-purple-700">Trade Ambassadors</p>
                <p className="text-2xl font-bold text-purple-900">{referralStats?.summary?.total_ambassadors || 0}</p>
              </Card>
            </div>

            {/* Program Config */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">Program Rules</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-blue-600">Referral Threshold</p>
                  <p className="font-bold text-blue-900">{referralStats?.config?.referral_threshold || 20} qualified referrals</p>
                </div>
                <div>
                  <p className="text-blue-600">Ambassador Slots per Country</p>
                  <p className="font-bold text-blue-900">{referralStats?.config?.ambassador_limit_per_country || 20} slots</p>
                </div>
                <div>
                  <p className="text-blue-600">Qualification Period</p>
                  <p className="font-bold text-blue-900">{referralStats?.config?.qualification_days || 30} days active + paid subscription</p>
                </div>
              </div>
            </Card>

            {/* Ambassadors by Country */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Founding Trade Ambassadors by Pacific Island Country
              </h3>
              <p className="text-sm text-slate-500 mb-4">First 20 sellers from each country to reach 20 qualified referrals earn the badge</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {referralStats?.ambassadors_by_country && Object.entries(referralStats.ambassadors_by_country).map(([country, data]) => (
                  <div key={country} className={`p-3 rounded-lg border ${data.current > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                    <p className="font-medium text-sm truncate" title={country}>{country}</p>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-slate-500">{data.current}/{data.limit}</span>
                      {data.remaining > 0 ? (
                        <span className="text-xs text-green-600">{data.remaining} slots</span>
                      ) : (
                        <span className="text-xs text-red-500">Full</span>
                      )}
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2">
                      <div 
                        className="bg-amber-500 h-1.5 rounded-full"
                        style={{ width: `${(data.current / data.limit) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Pending Referrals for Review */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Referrals Pending Review</h3>
              {referralStats?.pending_for_review?.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {referralStats.pending_for_review.map((ref) => (
                    <div key={ref.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div>
                        <p className="font-medium">{ref.referred_name}</p>
                        <p className="text-xs text-slate-500">{ref.referred_email} • {ref.referred_country}</p>
                        <p className="text-xs text-slate-400">Referred by: {ref.referrer_name}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs rounded ${ref.status === 'verified' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {ref.status}
                        </span>
                        <p className="text-xs text-slate-500 mt-1">
                          Deadline: {new Date(ref.qualification_deadline).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No referrals pending review</p>
              )}
            </Card>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
              <Button onClick={() => setShowAddUserModal(true)} data-testid="add-user-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>

            <Card className="p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">User</th>
                      <th className="text-left p-4 font-medium text-slate-600">Role</th>
                      <th className="text-left p-4 font-medium text-slate-600">Type</th>
                      <th className="text-left p-4 font-medium text-slate-600">Status</th>
                      <th className="text-left p-4 font-medium text-slate-600">Joined</th>
                      <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b hover:bg-slate-50">
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{u.full_name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            u.role === 'admin' ? 'bg-red-100 text-red-700' :
                            u.role === 'seller' ? 'bg-purple-100 text-purple-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {u.business_type || 'individual'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            {u.verified_email && <span className="w-2 h-2 bg-green-500 rounded-full" title="Email verified" />}
                            {u.verified_phone && <span className="w-2 h-2 bg-blue-500 rounded-full" title="Phone verified" />}
                            {u.verified_face && <span className="w-2 h-2 bg-purple-500 rounded-full" title="Face verified" />}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-slate-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedUserToDelete(u);
                              setShowDeleteUserModal(true);
                            }}
                            disabled={u.role === 'admin'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* P&L TAB */}
          <TabsContent value="pnl" className="space-y-6">
            <div className="flex justify-end mb-4">
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" /> Print for Sponsors
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-xl font-bold mb-6 text-green-700">Revenue</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span>Subscription Revenue</span>
                    <strong className="text-green-700">${(revenue * 0.7).toFixed(2)}</strong>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span>Credit Purchases</span>
                    <strong className="text-green-700">${(revenue * 0.2).toFixed(2)}</strong>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span>Other Revenue</span>
                    <strong className="text-green-700">${(revenue * 0.1).toFixed(2)}</strong>
                  </div>
                  <div className="border-t pt-4 flex justify-between items-center">
                    <span className="font-bold">Total Revenue</span>
                    <strong className="text-2xl text-green-700">${revenue.toFixed(2)}</strong>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-bold mb-6 text-red-700">Expenses</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span>SMS/WhatsApp Costs</span>
                    <strong className="text-red-700">-${(analytics?.api_costs?.sms || 0).toFixed(2)}</strong>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span>Face Verification API</span>
                    <strong className="text-red-700">-${(analytics?.api_costs?.face_verification || 0).toFixed(2)}</strong>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span>Freight API Costs</span>
                    <strong className="text-red-700">-${(costs * 0.3).toFixed(2)}</strong>
                  </div>
                  <div className="border-t pt-4 flex justify-between items-center">
                    <span className="font-bold">Total Expenses</span>
                    <strong className="text-2xl text-red-700">-${costs.toFixed(2)}</strong>
                  </div>
                </div>
              </Card>
            </div>

            <Card className="p-6 bg-gradient-to-r from-ocean-deep to-ocean text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold mb-2">Net Profit / Loss</h3>
                  <p className="text-white/80">Current Period</p>
                </div>
                <div className="text-right">
                  <p className={`text-4xl font-chivo font-black ${netProfit >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                    {netProfit >= 0 ? '+' : ''}${netProfit.toFixed(2)}
                  </p>
                  <p className="text-white/80">
                    {netProfit >= 0 ? 'Profit' : 'Loss'}
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4">User Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={userDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {userDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4">Revenue vs Costs</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[
                    { name: 'Revenue', value: revenue, fill: '#22c55e' },
                    { name: 'Costs', value: costs, fill: '#ef4444' },
                    { name: 'Profit', value: netProfit, fill: netProfit >= 0 ? '#3b82f6' : '#ef4444' }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                    <Bar dataKey="value" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>

            {/* HS Code Analytics Section */}
            <div className="mt-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Hash className="w-6 h-6 text-ocean-deep" />
                HS Code Analytics
              </h3>
              
              {/* HS Code Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="p-4 bg-gradient-to-br from-ocean-deep/10 to-ocean/10">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-ocean-deep rounded-lg">
                      <Hash className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Unique HS Codes Used</p>
                      <p className="text-2xl font-bold">{hsCodeAnalytics?.total_hs_codes_used || 0}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-amber-500 rounded-lg">
                      <Search className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Lookups</p>
                      <p className="text-2xl font-bold">{hsCodeAnalytics?.total_lookups || 0}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-500 rounded-lg">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Quotes with HS Code</p>
                      <p className="text-2xl font-bold">{hsCodeAnalytics?.quotes_with_hs_code || 0}</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Most Used HS Codes */}
                <Card className="p-6">
                  <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Most Used HS Codes
                  </h4>
                  {hsCodeAnalytics?.most_used_codes?.length > 0 ? (
                    <div className="space-y-3">
                      {hsCodeAnalytics.most_used_codes.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-ocean-deep">{item.hs_code}</span>
                              <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded">
                                {item.category}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1 truncate">{item.description}</p>
                          </div>
                          <div className="ml-4 text-right">
                            <span className="px-3 py-1 bg-ocean-deep text-white rounded-full text-sm font-medium">
                              {item.usage_count} uses
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Hash className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No HS codes used yet</p>
                      <p className="text-sm">Data will appear as sellers use the freight calculator</p>
                    </div>
                  )}
                </Card>

                {/* Category Distribution */}
                <Card className="p-6">
                  <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-purple-600" />
                    Export Categories
                  </h4>
                  {hsCodeAnalytics?.category_distribution?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart 
                        data={hsCodeAnalytics.category_distribution}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="category" type="category" width={100} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#0f172a" radius={[0, 4, 4, 0]}>
                          {hsCodeAnalytics.category_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No category data yet</p>
                    </div>
                  )}
                </Card>
              </div>

              {/* Most Searched Terms */}
              {hsCodeAnalytics?.most_searched_terms?.length > 0 && (
                <Card className="p-6 mt-6">
                  <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Search className="w-5 h-5 text-amber-600" />
                    Popular Search Terms
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {hsCodeAnalytics.most_searched_terms.map((item, index) => (
                      <span 
                        key={index}
                        className="px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 rounded-full text-sm font-medium flex items-center gap-2"
                      >
                        {item.term}
                        <span className="px-2 py-0.5 bg-amber-200 rounded-full text-xs">
                          {item.count}
                        </span>
                      </span>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Document Preview Modal */}
      <Dialog open={showDocumentPreview} onOpenChange={setShowDocumentPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedVerification?.type === 'face' ? 'Face Verification' : 'Business Verification'} Documents
            </DialogTitle>
          </DialogHeader>
          
          {selectedVerification && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-medium">{selectedVerification.user?.full_name}</p>
                <p className="text-sm text-slate-500">{selectedVerification.user?.email}</p>
              </div>
              
              {selectedVerification.type === 'face' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Live Photo (Selfie)</Label>
                    <div className="mt-2 border rounded-lg p-4 bg-slate-50 flex items-center justify-center min-h-[200px]">
                      {selectedVerification.live_photo ? (
                        <img 
                          src={`data:image/jpeg;base64,${selectedVerification.live_photo}`}
                          alt="Live Photo"
                          className="max-h-48 rounded"
                        />
                      ) : (
                        <div className="text-center text-slate-400">
                          <Image className="w-12 h-12 mx-auto mb-2" />
                          <p>No photo available</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>ID Document - Front ({selectedVerification.id_type?.replace('_', ' ') || 'Unknown'})</Label>
                    <div className="mt-2 border rounded-lg p-4 bg-slate-50 flex items-center justify-center min-h-[200px]">
                      {selectedVerification.id_document ? (
                        <img 
                          src={`data:image/jpeg;base64,${selectedVerification.id_document}`}
                          alt="ID Document Front"
                          className="max-h-48 rounded"
                        />
                      ) : (
                        <div className="text-center text-slate-400">
                          <FileText className="w-12 h-12 mx-auto mb-2" />
                          <p>No document available</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* ID Back (for license/national ID) */}
                  {selectedVerification.id_document_back && (
                    <div>
                      <Label>ID Document - Back</Label>
                      <div className="mt-2 border rounded-lg p-4 bg-slate-50 flex items-center justify-center min-h-[200px]">
                        <img 
                          src={`data:image/jpeg;base64,${selectedVerification.id_document_back}`}
                          alt="ID Document Back"
                          className="max-h-48 rounded"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Business Information */}
                  {(selectedVerification.business_name || selectedVerification.business_email || selectedVerification.country) && (
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-800 mb-3 flex items-center">
                        <Building2 className="w-4 h-4 mr-2" />
                        Business Information
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {selectedVerification.business_name && (
                          <div>
                            <Label className="text-purple-700 text-xs">Business Name</Label>
                            <p className="font-medium text-purple-900">{selectedVerification.business_name}</p>
                          </div>
                        )}
                        {selectedVerification.business_email && (
                          <div>
                            <Label className="text-purple-700 text-xs">Business Email {selectedVerification.email_verified && <CheckCircle className="w-3 h-3 inline text-green-600" />}</Label>
                            <p className="font-medium text-purple-900">{selectedVerification.business_email}</p>
                          </div>
                        )}
                        {selectedVerification.business_phone && (
                          <div>
                            <Label className="text-purple-700 text-xs">Business Phone</Label>
                            <p className="font-medium text-purple-900">{selectedVerification.business_phone}</p>
                          </div>
                        )}
                        {selectedVerification.country && (
                          <div>
                            <Label className="text-purple-700 text-xs">Country</Label>
                            <p className="font-medium text-purple-900">{selectedVerification.country}</p>
                          </div>
                        )}
                        {selectedVerification.address && (
                          <div className="col-span-2">
                            <Label className="text-purple-700 text-xs">Address</Label>
                            <p className="font-medium text-purple-900">
                              {selectedVerification.address}
                              {selectedVerification.city && `, ${selectedVerification.city}`}
                              {selectedVerification.postal_code && ` ${selectedVerification.postal_code}`}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Registration Details */}
                  {(selectedVerification.registration_number || selectedVerification.established_date || selectedVerification.expiry_date || selectedVerification.tax_number) && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-blue-50 p-4 rounded-lg">
                      {selectedVerification.registration_number && (
                        <div>
                          <Label className="text-blue-700 text-xs">Registration Number</Label>
                          <p className="font-medium text-blue-900">{selectedVerification.registration_number}</p>
                        </div>
                      )}
                      {selectedVerification.tax_number && (
                        <div>
                          <Label className="text-blue-700 text-xs">Tax Number</Label>
                          <p className="font-medium text-blue-900">{selectedVerification.tax_number}</p>
                        </div>
                      )}
                      {selectedVerification.established_date && (
                        <div>
                          <Label className="text-blue-700 text-xs">Established Date</Label>
                          <p className="font-medium text-blue-900">{selectedVerification.established_date}</p>
                        </div>
                      )}
                      {selectedVerification.expiry_date && (
                        <div>
                          <Label className="text-blue-700 text-xs">Expiry Date</Label>
                          <p className="font-medium text-blue-900">{selectedVerification.expiry_date}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Documents */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Business Document ({selectedVerification.document_type?.replace('_', ' ')})</Label>
                      <div className="mt-2 border rounded-lg p-4 bg-slate-50 flex items-center justify-center min-h-[200px]">
                        {selectedVerification.document_data ? (
                          <img 
                            src={`data:image/jpeg;base64,${selectedVerification.document_data}`}
                            alt="Business Document"
                            className="max-h-48 rounded"
                          />
                        ) : (
                          <div className="text-center text-slate-400">
                            <FileText className="w-12 h-12 mx-auto mb-2" />
                            <p>No document available</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {selectedVerification.tax_document && (
                      <div>
                        <Label>Tax Document</Label>
                        <div className="mt-2 border rounded-lg p-4 bg-slate-50 flex items-center justify-center min-h-[200px]">
                          <img 
                            src={`data:image/jpeg;base64,${selectedVerification.tax_document}`}
                            alt="Tax Document"
                            className="max-h-48 rounded"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDocumentPreview(false)}>Close</Button>
                <Button className="bg-green-600" onClick={() => {
                  setVerificationDecision('approve');
                  setShowDocumentPreview(false);
                  setShowDecisionModal(true);
                }}>
                  <CheckCircle className="w-4 h-4 mr-2" /> Approve
                </Button>
                <Button variant="destructive" onClick={() => {
                  setVerificationDecision('decline');
                  setShowDocumentPreview(false);
                  setShowDecisionModal(true);
                }}>
                  <XCircle className="w-4 h-4 mr-2" /> Decline
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Decision Modal */}
      <Dialog open={showDecisionModal} onOpenChange={setShowDecisionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {verificationDecision === 'approve' ? 'Approve' : 'Decline'} Verification
            </DialogTitle>
            <DialogDescription>
              {verificationDecision === 'approve' 
                ? 'The user will be notified via email.'
                : 'Please provide a reason for declining.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {verificationDecision === 'decline' && (
            <div>
              <Label>Reason for Decline</Label>
              <Textarea
                placeholder="Please provide a reason..."
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="mt-2"
              />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionModal(false)}>Cancel</Button>
            <Button 
              onClick={handleDecision}
              disabled={decidingLoading || (verificationDecision === 'decline' && !declineReason.trim())}
              className={verificationDecision === 'approve' ? 'bg-green-600' : ''}
              variant={verificationDecision === 'decline' ? 'destructive' : 'default'}
            >
              {decidingLoading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new user account manually</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={newUserForm.full_name}
                onChange={(e) => setNewUserForm({...newUserForm, full_name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                placeholder="••••••••"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role</Label>
                <Select value={newUserForm.role} onValueChange={(v) => setNewUserForm({...newUserForm, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="seller">Seller</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Business Type</Label>
                <Select value={newUserForm.business_type} onValueChange={(v) => setNewUserForm({...newUserForm, business_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserModal(false)}>Cancel</Button>
            <Button onClick={handleAddUser}>Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Modal */}
      <Dialog open={showDeleteUserModal} onOpenChange={setShowDeleteUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedUserToDelete && (
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="font-medium">{selectedUserToDelete.full_name}</p>
              <p className="text-sm text-slate-500">{selectedUserToDelete.email}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteUserModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              <Trash2 className="w-4 h-4 mr-2" /> Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
